// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/curves.js
//
// Response curves from different applications are stored and interpolated
// differently, so a curve is parsed with its source's rules and then baked
// into a dense, sorted point list. The renderer evaluates baked curves with
// plain linear interpolation, which matches the source spline to within
// 1/64 of the output range at the 33-sample resolution used here.
//
//   Krita     "0,0;0.5,0.7;1,1;"  natural cubic spline (KisCubicCurve)
//   Procreate {points:["{0.0, 0.0}", "{1.0, 1.0}"]}  stored unsorted;
//             interpolated here with a monotone cubic, which cannot
//             overshoot. Procreate's exact spline is not documented.
//   MyPaint   [[x, y], ...] in input units; piecewise linear (libmypaint).

export const BAKED_CURVE_SAMPLES = 33;

function finite(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function clamp01(value) {
  return Math.max(0, Math.min(1, finite(value)));
}

function cleanPoints(points) {
  const sorted = points
    .filter((p) => p && Number.isFinite(p.x) && Number.isFinite(p.y))
    .map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }))
    .sort((a, b) => a.x - b.x);
  const out = [];
  for (const point of sorted) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - point.x) < 1e-6) last.y = point.y;
    else out.push(point);
    if (out.length >= 256) break;
  }
  return out;
}

export function parseKritaCurve(text) {
  const points = String(text || '')
    .split(';')
    .map((pair) => pair.split(','))
    .filter((parts) => parts.length >= 2)
    .map((parts) => ({ x: Number(parts[0]), y: Number(parts[1]) }));
  return cleanPoints(points);
}

export function parseProcreateCurve(value) {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value.points : value;
  if (!Array.isArray(raw)) return [];
  const points = raw.map((item) => {
    if (typeof item === 'string') {
      const match = item.match(/\{\s*(-?[\d.eE+-]+)\s*,\s*(-?[\d.eE+-]+)\s*\}/);
      return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
    }
    if (Array.isArray(item) && item.length >= 2) return { x: Number(item[0]), y: Number(item[1]) };
    if (item && typeof item === 'object') return { x: Number(item.x), y: Number(item.y) };
    return null;
  });
  return cleanPoints(points);
}

export function isIdentityCurve(points) {
  if (!Array.isArray(points) || points.length < 2) return true;
  return points.every((p) => Math.abs(p.x - p.y) < 0.004);
}

// Natural cubic spline, the interpolation Krita's KisCubicSpline uses.
function naturalSpline(points) {
  const n = points.length;
  if (n < 3) return null;
  const h = [];
  for (let i = 0; i < n - 1; i += 1) h.push(Math.max(1e-6, points[i + 1].x - points[i].x));
  const alpha = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i += 1) {
    alpha[i] = (3 / h[i]) * (points[i + 1].y - points[i].y) - (3 / h[i - 1]) * (points[i].y - points[i - 1].y);
  }
  const l = new Array(n).fill(1);
  const mu = new Array(n).fill(0);
  const z = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i += 1) {
    l[i] = 2 * (points[i + 1].x - points[i - 1].x) - h[i - 1] * mu[i - 1];
    mu[i] = h[i] / l[i];
    z[i] = (alpha[i] - h[i - 1] * z[i - 1]) / l[i];
  }
  const c = new Array(n).fill(0);
  const b = new Array(n - 1).fill(0);
  const d = new Array(n - 1).fill(0);
  for (let j = n - 2; j >= 0; j -= 1) {
    c[j] = z[j] - mu[j] * c[j + 1];
    b[j] = (points[j + 1].y - points[j].y) / h[j] - (h[j] * (c[j + 1] + 2 * c[j])) / 3;
    d[j] = (c[j + 1] - c[j]) / (3 * h[j]);
  }
  return (x) => {
    let i = n - 2;
    for (let k = 0; k < n - 1; k += 1) {
      if (x <= points[k + 1].x) {
        i = k;
        break;
      }
    }
    const dx = x - points[i].x;
    return points[i].y + b[i] * dx + c[i] * dx * dx + d[i] * dx * dx * dx;
  };
}

// Fritsch-Carlson monotone cubic: smooth, never overshoots the data.
function monotoneSpline(points) {
  const n = points.length;
  if (n < 3) return null;
  const dx = [];
  const slope = [];
  for (let i = 0; i < n - 1; i += 1) {
    dx.push(Math.max(1e-6, points[i + 1].x - points[i].x));
    slope.push((points[i + 1].y - points[i].y) / dx[i]);
  }
  const m = [slope[0]];
  for (let i = 1; i < n - 1; i += 1) {
    m.push(slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2);
  }
  m.push(slope[n - 2]);
  for (let i = 0; i < n - 1; i += 1) {
    if (slope[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * slope[i];
      m[i + 1] = t * b * slope[i];
    }
  }
  return (x) => {
    let i = n - 2;
    for (let k = 0; k < n - 1; k += 1) {
      if (x <= points[k + 1].x) {
        i = k;
        break;
      }
    }
    const h = dx[i];
    const t = (x - points[i].x) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * points[i].y + (t3 - 2 * t2 + t) * h * m[i] + (-2 * t3 + 3 * t2) * points[i + 1].y + (t3 - t2) * h * m[i + 1];
  };
}

function linear(points) {
  return (x) => {
    if (x <= points[0].x) return points[0].y;
    for (let i = 1; i < points.length; i += 1) {
      if (x <= points[i].x) {
        const span = Math.max(1e-6, points[i].x - points[i - 1].x);
        return points[i - 1].y + (points[i].y - points[i - 1].y) * ((x - points[i - 1].x) / span);
      }
    }
    return points[points.length - 1].y;
  };
}

// interpolation: 'krita' | 'procreate' | 'linear'
export function bakeCurve(points, interpolation = 'linear', samples = BAKED_CURVE_SAMPLES) {
  const clean = cleanPoints(Array.isArray(points) ? points : []);
  if (clean.length < 2) return [];
  const spline = interpolation === 'krita'
    ? naturalSpline(clean)
    : interpolation === 'procreate'
      ? monotoneSpline(clean)
      : null;
  const fn = spline || linear(clean);
  const first = clean[0];
  const last = clean[clean.length - 1];
  const out = [];
  for (let i = 0; i < samples; i += 1) {
    const x = i / (samples - 1);
    const y = x <= first.x ? first.y : x >= last.x ? last.y : fn(x);
    out.push({ x: Number(x.toFixed(5)), y: Number(clamp01(y).toFixed(5)) });
  }
  return out;
}

export function evaluateBakedCurve(points, input, fallback = input) {
  if (!Array.isArray(points) || !points.length) return fallback;
  const x = clamp01(input);
  if (x <= points[0].x) return points[0].y;
  for (let i = 1; i < points.length; i += 1) {
    const next = points[i];
    if (x <= next.x) {
      const prev = points[i - 1];
      const span = Math.max(1e-6, next.x - prev.x);
      return prev.y + (next.y - prev.y) * ((x - prev.x) / span);
    }
  }
  return points[points.length - 1].y;
}
